import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { getAICategoryRankings } from 'apis/RecommendationsAPI';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import PillSwitcher from 'components/lib/PillSwitcher';
import { getOrdinalSuffix } from 'components/View/utils';
import { AICompetitiveIntelligenceRelatedDataType } from 'types/recommendations';

import AiCiSectionIcon from './AiCiSectionIcon';
import CategoryActionPlans from './CategoryActionPlans';
import CategoryInsightSections from './CategoryInsightSections';
import CategoryRankingMeta from './CategoryRankingMeta';
import { AI_CI_RANKING_TABS, AICIRankingTabKey, OVERALL_VIEW_KEY } from './constants';
import RankingsTable from './RankingsTable';
import { findAICIRelatedDataByTabLabel, getActionPlanSourceInsightIdByTab, getAICIRankingTabRank } from './utils';

const EmptyMessage = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  padding: 16px 0;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 24px;
`;

const Title = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  align-items: center;
  font-size: 20px;
  font-family: var(--font-bold);
  margin-bottom: 10px;
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RankText = styled.span`
  color: var(--text-secondary);
  font-size: 10px;
  &.active {
    color: inherit;
  }
`;
type Props = {
  communityId?: number;
  overallRanking?: number | null;
  primaryCommunityName?: string | null;
  relatedData?: AICompetitiveIntelligenceRelatedDataType[];
  activeTab: AICIRankingTabKey;
  onActiveTabChange: (tab: AICIRankingTabKey) => void;
};

const MarketCategoryBreakdown = ({
  communityId,
  overallRanking,
  primaryCommunityName,
  relatedData = [],
  activeTab,
  onActiveTabChange,
}: Props) => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['ai-ci-category-rankings', communityId, activeTab],
    queryFn: () => getAICategoryRankings(communityId ?? -1, activeTab),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
    keepPreviousData: false,
  });

  const rows = data?.rows ?? [];

  const categoryTab = activeTab === OVERALL_VIEW_KEY ? null : activeTab;
  const activeTabLabel = AI_CI_RANKING_TABS.find((tab) => tab.key === activeTab)?.label;
  const selectedRelatedData =
    categoryTab && activeTabLabel ? findAICIRelatedDataByTabLabel(relatedData, activeTabLabel) : undefined;
  const selectedRecommendationId =
    categoryTab && selectedRelatedData ? selectedRelatedData.recommendation_id ?? null : null;
  const selectedSourceInsightId = categoryTab ? getActionPlanSourceInsightIdByTab(categoryTab) : null;

  const tabItems = AI_CI_RANKING_TABS.map((tab) => {
    const ranking = getAICIRankingTabRank(tab, relatedData, overallRanking);

    return {
      key: tab.key,
      label: (
        <TabLabel>
          <span>{tab.label}</span>
          {ranking != null && (
            <RankText className={activeTab === tab.key ? 'active' : undefined}>{getOrdinalSuffix(ranking)}</RankText>
          )}
        </TabLabel>
      ),
    };
  });

  const handlePrimaryDetailsClick = () => {
    if (!selectedRecommendationId) return;
    navigate(`/ai-competitive-intelligence/${selectedRecommendationId}`);
  };

  return (
    <div>
      <Title>
        <AiCiSectionIcon src="/icons/format-list-bulleted.svg" />
        <div>Ranking</div>
      </Title>
      <PillSwitcher
        items={tabItems}
        activeKey={activeTab}
        onChange={(k) => onActiveTabChange(k as AICIRankingTabKey)}
      />
      {isLoading && (
        <LoaderContainer>
          <LoaderRibbed active />
        </LoaderContainer>
      )}
      {!isLoading && rows.length === 0 && <EmptyMessage>No data for this category yet.</EmptyMessage>}
      {!isLoading && rows.length > 0 && (
        <>
          <RankingsTable
            rows={rows}
            primaryCommunityName={primaryCommunityName}
            showPrimaryDetailsButton={Boolean(categoryTab && selectedRecommendationId)}
            onPrimaryDetailsClick={handlePrimaryDetailsClick}
            bare
          />
          {categoryTab && (
            <CategoryRankingMeta
              communityId={communityId}
              categoryLabel={activeTabLabel}
              relatedData={selectedRelatedData}
            />
          )}
          {categoryTab && selectedRecommendationId && (
            <CategoryInsightSections
              recommendationId={selectedRecommendationId}
              communityId={communityId}
              categoryLabel={activeTabLabel}
            />
          )}
          {categoryTab && (
            <CategoryActionPlans
              communityId={communityId}
              categoryLabel={activeTabLabel}
              sourceInsightId={selectedSourceInsightId}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MarketCategoryBreakdown;
